import { useState } from "react";
import { FiPhone, FiMail, FiGlobe, FiMapPin } from "react-icons/fi";

import FallbackImage from "components/FallbackImage/FallbackImage";
import AccordionLargeTextDisplay from "newui/components/AccordionLargeTextDisplay";
import { Modal } from "newui/components/ui/Modal";
import TextBoxHTML from "newui/components/TextBoxHTML";
import SkeletonBase from "newui/components/Skeleton";

import type { Company } from "newui/features/Company/types";

interface CompanyHeaderProps {
	company: Company;
	isLoading: boolean;
}

export const CompanyHeader: React.FC<CompanyHeaderProps> = ({
	company,
	isLoading,
}) => {
	const [aboutModalOpen, setAboutModalOpen] = useState(false);

	const globalPresence = company.global_presence?.map((g) => g.name) ?? [];
	const primaryAddress = company.locations?.[0]?.address;

	if (isLoading) {
		return (
			<div className="flex flex-col gap-4 border-b border-gray-200 p-4 lg:hidden">
				<div className="flex items-center gap-4">
					<SkeletonBase width="56px" height="56px" />
					<SkeletonBase width="200px" height="24px" />
				</div>
				<SkeletonBase width="100%" height="40px" />
			</div>
		);
	}

	return (
		<div className="border-b border-gray-200 p-4 lg:hidden">
			{/* Top row: Logo + Name */}
			<div className="flex items-center gap-4">
				<div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg border border-gray-100">
					<FallbackImage
						src={company.logo_path}
						alt={company.name}
						className="h-full w-full object-contain"
					/>
				</div>
				<div className="min-w-0 flex-1">
					<h1 className="truncate font-roboto text-lg font-semibold text-gray-900">
						{company.name}
					</h1>
					{company.years_trading && (
						<p className="text-xs text-gray-500">
							{company.years_trading} years trading
						</p>
					)}
				</div>
			</div>

			{/* Description */}
			<div className="mt-3">
				<AccordionLargeTextDisplay
					text={company.description}
					title="About"
					maxTextDisplay={100}
					isLoading={false}
				/>
			</div>

			{/* Contact row */}
			<div className="mt-3 flex flex-col gap-2 text-xs">
				{company.phone && (
					<a
						href={`tel:${company.phone}`}
						className="flex items-center gap-1.5 text-blue-600 underline decoration-blue-300 underline-offset-2 hover:text-blue-800 hover:decoration-blue-600"
					>
						<FiPhone size={12} className="shrink-0" />
						<span>{company.phone}</span>
					</a>
				)}
				{company.email && (
					<a
						href={`mailto:${company.email}`}
						className="flex items-center gap-1.5 text-blue-600 underline decoration-blue-300 underline-offset-2 hover:text-blue-800 hover:decoration-blue-600"
					>
						<FiMail size={12} className="shrink-0" />
						<span className="break-all">{company.email}</span>
					</a>
				)}
				{primaryAddress && (
					<span className="flex items-center gap-1.5 text-gray-500">
						<FiMapPin size={12} className="shrink-0" />
						<span className="break-words">{primaryAddress}</span>
					</span>
				)}
				{company.website && (
					<a
						href={
							company.website.startsWith("http")
								? company.website
								: `https://${company.website}`
						}
						target="_blank"
						rel="noopener noreferrer"
						className="flex items-center gap-1.5 text-blue-600 underline decoration-blue-300 underline-offset-2 hover:text-blue-800 hover:decoration-blue-600"
					>
						<FiGlobe size={12} className="shrink-0" />
						<span className="break-all">{company.website}</span>
					</a>
				)}
			</div>

			{/* Global Presence */}
			{globalPresence.length > 0 && (
				<p className="mt-2 text-xs text-gray-500">
					Global Presence:{" "}
					<span className="font-medium text-gray-700">
						{globalPresence.join(", ")}
					</span>
				</p>
			)}

			{/* About Modal */}
			<Modal
				isOpen={aboutModalOpen}
				onClose={() => setAboutModalOpen(false)}
				title={company.name}
				size="xl"
			>
				{company.banner_path && (
					<FallbackImage
						src={company.banner_path}
						alt={`${company.name} banner`}
						className="mb-4 h-48 w-full rounded-lg object-cover"
					/>
				)}
				<TextBoxHTML html={company.description} />
			</Modal>
		</div>
	);
};
